import { ROUTES } from "@lhd/constants";
import { user } from "@lhd/features/users/selector";
import {
  setCompanyId,
  sleep,
  getEffectiveIsDisplayInNewTab,
  IS_DISPLAY_IN_NEW_TAB_PARAM,
} from "@lhd/utility";
import { get, map } from "lodash";
import { FC, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isMobileOnly } from "react-device-detect";
import LogoCompany from "./components/LogoCompany";
import SearchPopup from "./components/SearchPopup";
import CustomVerticalScrollBar from "@lhd/components/CustomVerticalScrollBar";
import {
  Styles,
  SearchButtonWrapper,
  SearchButtonContainer,
  SearchIconWrapper,
  WrapperSideBarCompany,
  StyledSpinWrapper,
  SearchText,
  SortableItemWrapper,
} from "./styled";
import {
  useReAuthenticationOneTime,
  useUpdatePositionCompanies,
  useGlobalLoading,
} from "@lhd/hooks";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Spin, Tooltip } from "antd";
import { FaUser } from "react-icons/fa";
import { useConfirmingLeave } from "@lhd/app/index";

interface SideBarCompanyListProps {
  isCollapse: boolean;
  divHeight: number;
}

const SortableItem = ({ company, isDragging, onSelectCompany }: any) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: company.id });

  return (
    <SortableItemWrapper
      ref={setNodeRef}
      $transform={CSS.Transform.toString(
        transform ? { ...transform, x: 0 } : null,
      )}
      $transition={transition}
      $isDragging={isDragging}
      {...attributes}
      {...listeners}
      onPointerUp={(event) => {
        event.preventDefault();
        if (isDragging) return;
        onSelectCompany(company);
      }}
    >
      <LogoCompany company={company} />
    </SortableItemWrapper>
  );
};

const SideBarCompanyList: FC<SideBarCompanyListProps> = ({ isCollapse }) => {
  const navigate = useNavigate();
  const { confirmAction } = useConfirmingLeave();
  const auth = useSelector(user);
  const { handleAuthen } = useReAuthenticationOneTime(false);
  const [companies, setCompanies] = useState<any>([]);
  const { mutate, isLoading } = useUpdatePositionCompanies();
  const [isDragging, setIsDragging] = useState(false);
  const [isClickLoading, setIsClickLoading] = useState(false);
  const { isLoading: isGlobalLoading } = useGlobalLoading({
    urlPatterns: ["permission", "/me"],
  });
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isSearchPopupOpen, setIsSearchPopupOpen] = useState(false);
  const searchButtonRef = useRef<HTMLDivElement>(null);
  const companiesListRef = useRef<HTMLDivElement>(null);

  const [searchParams] = useSearchParams();
  const isDisplayInNewTab = getEffectiveIsDisplayInNewTab(
    searchParams.get(IS_DISPLAY_IN_NEW_TAB_PARAM) === "true",
  );

  useEffect(() => {
    if (!auth?.companies || isLoading) return;
    setCompanies(
      map(auth.companies, (company) => ({
        name: get(company, "name", ""),
        logo: company.logo_original_url,
        isWorkDefault: company.is_work_default,
        id: get(company, "id"),
      })),
    );
  }, [auth?.companies, isLoading]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isSearchPopupOpen &&
        searchButtonRef.current &&
        !searchButtonRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest(".search-popup-wrapper")
      ) {
        setIsSearchPopupOpen(false);
        setIsSearchActive(false);
      }
    };

    if (isSearchPopupOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSearchPopupOpen]);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (event: any) => {
    setTimeout(() => setIsDragging(false), 100);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = companies.findIndex((c: any) => c.id === active.id);
    const newIndex = companies.findIndex((c: any) => c.id === over.id);

    const newCompanies = [...companies];
    const [movedItem] = newCompanies.splice(oldIndex, 1);
    newCompanies.splice(newIndex, 0, movedItem);
    const companyIds = newCompanies.map((company) => company.id);
    setCompanies(newCompanies);

    mutate(companyIds, {
      onSuccess: () => {
        setCompanies(newCompanies);
      },
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 7,
      },
    }),
  );

  const handleSearchClick = () => {
    const newState = !isSearchActive;
    setIsSearchActive(newState);
    setIsSearchPopupOpen(newState);
  };

  const changeCompany = async (company: any) => {
    setIsClickLoading(true);
    setIsSearchActive(false);
    setIsSearchPopupOpen(false);
    setCompanyId(company.id.toString());
    handleAuthen(company.id);
    await sleep(300);
    setIsClickLoading(false);
    navigate(`/${ROUTES.CORRELATION_DIAGRAM}`);
  };

  const handleSelectCompany = (company: any) => {
    confirmAction(() => changeCompany(company));
  };

  const handleCloseSearchPopup = () => {
    setIsSearchPopupOpen(false);
    setIsSearchActive(false);
  };

  if (isDisplayInNewTab) return <></>;

  return (
    <Styles $isMobile={isMobileOnly}>
      <WrapperSideBarCompany
        id="wrapper-side_bar_company"
        className="wrapper-side_bar_company"
        $isMobile={isMobileOnly}
      >
        <StyledSpinWrapper>
          <Spin
            spinning={isLoading || isClickLoading || isGlobalLoading}
            size="large"
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragMove={(event) => {
                if (event.active && event.delta) {
                  event.delta.x = 0;
                }
              }}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={companies.map((c: any) => c.id)}
                strategy={verticalListSortingStrategy}
                disabled={isLoading || isClickLoading || isGlobalLoading}
              >
                <div className="container-side_bar_company">
                  <div
                    style={{
                      display: "flex",
                      position: "relative",
                      flex: 1,
                      minHeight: 0,
                    }}
                  >
                    <CustomVerticalScrollBar
                      element={companiesListRef.current}
                      isCustom
                    />
                    <div
                      ref={companiesListRef}
                      className="companies-list-scrollable"
                    >
                      <Tooltip title="開発中" color="#6BC77A">
                        <div className="my-page-button-wrapper">
                          <Button className="my-page-button" disabled>
                            <FaUser fontSize={16} />
                            マイページ
                          </Button>
                        </div>
                      </Tooltip>
                      <SearchButtonWrapper $isMobile={isMobileOnly}>
                        <SearchButtonContainer
                          ref={searchButtonRef}
                          $isActive={isSearchActive}
                          $isMobile={isMobileOnly}
                          onClick={handleSearchClick}
                        >
                          <SearchIconWrapper
                            $isActive={isSearchActive}
                            $isMobile={isMobileOnly}
                          >
                            <SearchText $isMobile={isMobileOnly}>
                              検索
                            </SearchText>
                          </SearchIconWrapper>
                        </SearchButtonContainer>
                      </SearchButtonWrapper>
                      {companies.map((company: any) => (
                        <SortableItem
                          key={company.id}
                          company={company}
                          isDragging={isDragging}
                          onSelectCompany={handleSelectCompany}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </SortableContext>
            </DndContext>
          </Spin>
        </StyledSpinWrapper>
      </WrapperSideBarCompany>
      <SearchPopup
        isOpen={isSearchPopupOpen}
        companies={companies}
        onSelectCompany={handleSelectCompany}
        onClose={handleCloseSearchPopup}
      />
    </Styles>
  );
};

export default SideBarCompanyList;
